import type {
  BalanceCorrectionEvent,
  ChargeEvent,
  DisbursementEvent,
  EmiDateChangeEvent,
  Loan,
  LoanEvent,
  LoanEventType,
  PrepaymentEvent,
  RateChangeEvent,
} from '../types';
import { EVENT_TYPE_LABELS } from '../types';
import { validateEvent, validateLoanInput } from './calc/validation';

/**
 * ============================================================================
 * BACKUP FILE FORMAT
 * ============================================================================
 *
 * A backup is a complete, self-contained snapshot of the app's entire
 * local-first dataset: the one Loan record and the full history of all
 * six event types, INCLUDING superseded (soft-deleted/edited-over) rows
 * — a restore recreates the full audit trail, not just the current
 * active view. This is the single shared format for both local
 * (download-to-file) and any future remote (Google Drive) backup — see
 * the Stage 1 architecture assessment; nothing here is Drive-specific,
 * and nothing else in the app should independently reimplement
 * serialization or validation of this data.
 *
 * `backupFormatVersion` is this JSON file's own version, deliberately
 * separate from Dexie's `db.verno` (the IndexedDB schema version, see
 * src/lib/db/schema.ts). They answer different questions: this field
 * gates whether restoreBackup() knows how to interpret the file at all;
 * `dexieSchemaVersion` (supplied by the caller — see createBackup below)
 * is carried through for forward reference only and is not itself
 * checked here — Dexie's own `.stores()` versioning mechanism handles
 * IndexedDB-side migrations independently.
 */

export const BACKUP_FORMAT_VERSION = 1 as const;

export interface BackupEventCollections {
  disbursement: DisbursementEvent[];
  rate_change: RateChangeEvent[];
  prepayment: PrepaymentEvent[];
  balance_correction: BalanceCorrectionEvent[];
  charge: ChargeEvent[];
  emi_date_change: EmiDateChangeEvent[];
}

export interface BackupFile {
  backupFormatVersion: typeof BACKUP_FORMAT_VERSION;
  dexieSchemaVersion: number;
  exportedAt: string;
  loan: Loan | null;
  events: BackupEventCollections;
}

/** Every event type, derived from EVENT_TYPE_LABELS rather than a second hardcoded list. */
const ALL_EVENT_TYPES = Object.keys(EVENT_TYPE_LABELS) as LoanEventType[];

function emptyEventCollections(): BackupEventCollections {
  return {
    disbursement: [],
    rate_change: [],
    prepayment: [],
    balance_correction: [],
    charge: [],
    emi_date_change: [],
  };
}

/**
 * Builds a backup file object from an already-fetched loan and event
 * list. Pure and synchronous — does not read the database itself (not
 * even for the schema version, which the caller supplies via
 * `dexieSchemaVersion` — see below), so it is trivially unit-testable
 * with fixture data in complete isolation, matching this project's
 * existing pure-function testing style (see src/lib/calc/__tests__/).
 *
 * `events` MUST include superseded rows (i.e. callers should fetch via
 * `listEvents({ loanId, includeSuperseded: true })`) — this function
 * does not filter or re-fetch anything, it just buckets whatever it's
 * given by type.
 *
 * `dexieSchemaVersion` should be `db.verno` from the live Dexie
 * instance (see src/lib/db/schema.ts) — passed in by the caller rather
 * than read here, so this module has no dependency on a live database
 * connection at all and can be imported/tested standalone.
 */
export function createBackup(loan: Loan | undefined, events: LoanEvent[], dexieSchemaVersion: number): BackupFile {
  const collections = emptyEventCollections();

  for (const event of events) {
    switch (event.type) {
      case 'disbursement':
        collections.disbursement.push(event);
        break;
      case 'rate_change':
        collections.rate_change.push(event);
        break;
      case 'prepayment':
        collections.prepayment.push(event);
        break;
      case 'balance_correction':
        collections.balance_correction.push(event);
        break;
      case 'charge':
        collections.charge.push(event);
        break;
      case 'emi_date_change':
        collections.emi_date_change.push(event);
        break;
    }
  }

  return {
    backupFormatVersion: BACKUP_FORMAT_VERSION,
    dexieSchemaVersion,
    exportedAt: new Date().toISOString(),
    loan: loan ?? null,
    events: collections,
  };
}

export interface BackupValidationSuccess {
  valid: true;
  backup: BackupFile;
}

export interface BackupValidationFailure {
  valid: false;
  errors: string[];
}

export type BackupValidationResult = BackupValidationSuccess | BackupValidationFailure;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Validates an arbitrary parsed-JSON value as a well-formed, restorable
 * backup file. Two layers of checking:
 *
 *   1. STRUCTURAL — is this even shaped like a backup file at all (right
 *      top-level keys, recognized format version, events grouped as six
 *      arrays)? Checked here, since nothing elsewhere in the app has an
 *      equivalent check to reuse.
 *
 *   2. CONTENT — is the loan record and is every individual event
 *      actually valid per this app's own business rules? Delegated
 *      entirely to the EXISTING validateLoanInput()/validateEvent() from
 *      src/lib/calc/validation.ts — the same functions the live forms
 *      use — rather than re-implementing field-level validation here.
 *      Both functions only check business-meaningful fields (never
 *      id/createdAt/supersededAt), so they are safe to run unchanged
 *      against a stored/restored record, including superseded rows.
 *
 * Returns every error found (not just the first), so a restore attempt
 * against a malformed or incompatible file can show the user a complete
 * picture rather than a single cryptic message.
 */
export function validateBackupFile(data: unknown): BackupValidationResult {
  const errors: string[] = [];

  if (!isPlainObject(data)) {
    return { valid: false, errors: ['This file is not a valid backup (not a JSON object).'] };
  }

  if (data.backupFormatVersion !== BACKUP_FORMAT_VERSION) {
    return {
      valid: false,
      errors: [
        `This backup file's format (version ${String(data.backupFormatVersion)}) is not recognized. Expected version ${BACKUP_FORMAT_VERSION}.`,
      ],
    };
  }

  if (typeof data.dexieSchemaVersion !== 'number') {
    errors.push('Backup is missing a valid database schema version.');
  }

  if (typeof data.exportedAt !== 'string' || Number.isNaN(Date.parse(data.exportedAt))) {
    errors.push('Backup is missing a valid export date.');
  }

  if (data.loan !== null && !isPlainObject(data.loan)) {
    errors.push('Backup loan record is malformed.');
  }

  if (!isPlainObject(data.events)) {
    errors.push('Backup is missing its event data.');
    return { valid: false, errors };
  }

  const events = data.events;
  for (const type of ALL_EVENT_TYPES) {
    if (!Array.isArray(events[type])) {
      errors.push(`Backup is missing the "${EVENT_TYPE_LABELS[type]}" event list.`);
    }
  }

  // Stop here if the structural shape itself is broken — content
  // validation below assumes the shape checks above already passed.
  if (errors.length > 0) {
    return { valid: false, errors };
  }

  if (data.loan !== null) {
    const loanErrors = validateLoanInput(data.loan as Partial<Loan>);
    errors.push(...loanErrors.map((e) => `Loan: ${e}`));
  }

  for (const type of ALL_EVENT_TYPES) {
    const list = events[type] as unknown[];
    list.forEach((event, index) => {
      if (!isPlainObject(event)) {
        errors.push(`${EVENT_TYPE_LABELS[type]} #${index + 1}: not a valid event record.`);
        return;
      }
      const eventErrors = validateEvent(type, event as Partial<LoanEvent>);
      eventErrors.forEach((e) => errors.push(`${EVENT_TYPE_LABELS[type]} #${index + 1}: ${e}`));
    });
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  return { valid: true, backup: data as unknown as BackupFile };
}
